import { json } from '@codemirror/lang-json';
import { IconButton, SideSheet, Text, Tooltip } from '@digibee/beehive-ui';
import { iff } from '@digibee/control-statements';
import CodeMirror, { EditorView } from '@uiw/react-codemirror';
import { Copy } from 'iconoir-react';
import moment from 'moment';
import PropTypes from 'prop-types';

import * as Elements from './LogDetails.elements';
import Loading from '../Loading';

import i18n from '~/common/helpers/i18n';
import { CopyToClipboard } from '~/components/CopyToClipboard';
import codeEditorTheme from '~/scenes/Build/scenes/FlowBuilder/components/Design/panels/Debugger/common/codeEditorTheme';

const extension = [json(), EditorView.lineWrapping];

const LogDetails = ({ open, close, log, loading, logMessage }) => (
  <SideSheet
    header={i18n.t(
      'scenes.pipeline_logs.labels.upper_and_capitalize.capitalized',
      {
        item: `${i18n.t('common.labels.log_details')}`
      }
    )}
    onClose={close}
    open={open}
    size='small'
    data-testid='monitor-pipeline-logs-log-details-sidesheet-header'
  >
    {iff(open && loading, () => (
      <Elements.WrapperLoading>
        <Loading />
      </Elements.WrapperLoading>
    ))}
    {iff(open && !loading, () => (
      <Elements.Content>
        <SideSheet.Section>
          <Text
            as='p'
            css={{ fontWeight: '$bold', textAlign: 'left' }}
            data-testid='monitor-pipeline-logs-log-details-component-text-log-timestamp'
          >
            {i18n.t('scenes.pipeline_logs.labels.log_timestamp')}
          </Text>
          <Text
            as='p'
            css={{ textAlign: 'left' }}
            data-testid='monitor-pipeline-logs-log-details-component-value-log-timestamp'
          >
            {moment(log?.timestamp).format('DD/MM/YYYY HH:mm:ss:SSSS')}
          </Text>
          <Elements.Space />
          <Text
            as='p'
            css={{
              fontWeight: '$bold',
              textAlign: 'left'
            }}
            className='fs-mask'
            data-testid='monitor-pipeline-logs-log-details-component-pipeline-name'
          >
            {i18n.t(i18n.t('noun.pipeline_name'))}
          </Text>
          <Text
            as='p'
            css={{ textAlign: 'left' }}
            data-testid='monitor-pipeline-logs-log-details-component-value-pipeline-name'
          >
            {log?.pipelineName}
          </Text>
          <Elements.Space />
          <Text
            as='p'
            css={{
              fontWeight: '$bold',
              textAlign: 'left'
            }}
            data-testid='monitor-pipeline-logs-log-details-component-text-log-level'
          >
            {i18n.t(i18n.t('scenes.pipeline_logs.labels.log_level'))}
          </Text>
          <Text
            as='p'
            css={{ textAlign: 'left' }}
            data-testid='monitor-pipeline-logs-log-details-component-value-log-level'
          >
            {log?.logLevel}
          </Text>
          <Elements.Space />
          <Text
            as='p'
            css={{
              fontWeight: '$bold',
              textAlign: 'left'
            }}
            data-testid='monitor-pipeline-logs-log-details-component-text-log-message'
          >
            {i18n.t('scenes.pipeline_logs.labels.log_message')}
            <CopyToClipboard
              text={logMessage}
              data-testid='monitor-pipeline-logs-log-details-component-copy-to-clipboard-log-message'
            >
              <IconButton data-testid='monitor-pipeline-logs-button-copy-log-details-log-message'>
                <Tooltip
                  align='center'
                  content={i18n.t('common.actions.copy_log_message')}
                  side='right'
                >
                  <Copy
                    className='icon-smallest'
                    data-testid='monitor-pipeline-logs-icon-copy-log-details-log-message'
                  />
                </Tooltip>
              </IconButton>
            </CopyToClipboard>
          </Text>
          <CodeMirror
            theme={codeEditorTheme}
            height='500px'
            value={logMessage}
            extensions={extension}
            readOnly
            basicSetup={{
              tabSize: 2,
              lintKeymap: false,
              searchKeymap: false
            }}
            data-testid='monitor-pipeline-logs-log-details-component-text-log-logMessage'
          />
        </SideSheet.Section>
      </Elements.Content>
    ))}
  </SideSheet>
);

LogDetails.defaultProps = {
  open: false,
  close: () => {},
  log: null,
  loading: PropTypes.bool
};

LogDetails.propTypes = {
  open: PropTypes.bool,
  close: PropTypes.func,
  log: PropTypes.objectOf({}),
  loading: PropTypes.bool,
  logMessage: PropTypes.string.isRequired
};

export default LogDetails;
